import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import { CHAIN_IDS } from "../constants";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_INFO_QUERY_KEY = "wp:token-info";

/** wallet-server API가 네이티브 자산에 쓰는 sentinel 주소 (send/types.ts와 동일 값). */
const NATIVE_TOKEN_SENTINEL_ADDRESS =
  "0x0000000000000000000000000000000000000001";

interface TokenInfo {
  id: number;
  chain_id: number;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  image: string;
}

interface TokenInfoResponse {
  code: number;
  message: string;
  data: TokenInfo[];
}

export function useTokenInfo(env: Environment, enabled = true) {
  const crossChainId = CHAIN_IDS[env].cross;

  const { data, ...rest } = useQuery<TokenInfoResponse>({
    queryKey: [TOKEN_INFO_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/token/info`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) throw new Error(`Failed to fetch token info: ${res.status}`);
      const json: TokenInfoResponse = await res.json();
      if (json.code !== 200) throw new Error(`Token info API error: ${json.code}`);
      return json;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const { byChainAddr, byChainSym } = useMemo(() => {
    const addrMap = new Map<string, TokenInfo>();
    const symMap = new Map<string, TokenInfo>();
    if (!data?.data) return { byChainAddr: addrMap, byChainSym: symMap };

    for (const t of data.data) {
      const addr = t.address.toLowerCase();
      addrMap.set(`${t.chain_id}:${addr}`, t);
      addrMap.set(addr, t);

      const sym = t.symbol.replace(/^t/, "").toUpperCase();
      const chainSymKey = `${t.chain_id}:${sym}`;
      if (!symMap.has(chainSymKey)) symMap.set(chainSymKey, t);
      if (!symMap.has(sym)) symMap.set(sym, t);
    }

    return { byChainAddr: addrMap, byChainSym: symMap };
  }, [data]);

  /**
   * wallet token info API 기준 심볼. 주소 매칭 실패 시 fallback을 그대로
   * 반환한다 (API에 없는 토큰은 소비처가 가진 심볼 유지).
   * `chainOnly`면 CROSS 체인(현 env의 chain_id) 엔트리만 보고, 다른 체인
   * 엔트리로의 폴백을 막는다 (예: BSC 쪽 CROSS 오매칭 방지).
   */
  const getTokenSymbol = (
    address: string,
    fallback?: string,
    opts?: { chainOnly?: boolean },
  ): string | undefined => {
    const addr = address.toLowerCase();
    const token =
      byChainAddr.get(`${crossChainId}:${addr}`) ??
      (opts?.chainOnly ? undefined : byChainAddr.get(addr));
    return token?.symbol || fallback;
  };

  /**
   * 현 env CROSS 체인의 네이티브 토큰 엔트리. wallet-server API는 네이티브
   * 자산을 sentinel 주소(0x…0001)로 등록하므로 그 주소로 매칭한다.
   * WCROSS(wrapped native)의 표시 심볼/아이콘을 API 표기(stg=tONE,
   * prod=ONE)로 해석할 때 사용. API에 엔트리 없으면 undefined.
   */
  const getNativeToken = (): TokenInfo | undefined =>
    byChainAddr.get(`${crossChainId}:${NATIVE_TOKEN_SENTINEL_ADDRESS}`);

  const getTokenImage = (
    address: string,
    symbol?: string,
    opts?: { chainOnly?: boolean },
  ): string => {
    const addr = address.toLowerCase();
    const chainOnly = !!opts?.chainOnly;

    const byChain = byChainAddr.get(`${crossChainId}:${addr}`);
    if (byChain?.image) return byChain.image;

    if (!chainOnly) {
      const byAddr = byChainAddr.get(addr);
      if (byAddr?.image) return byAddr.image;
    }

    if (symbol) {
      const sym = symbol.replace(/^t/, "").toUpperCase();
      const byChainSymbol = byChainSym.get(`${crossChainId}:${sym}`);
      if (byChainSymbol?.image) return byChainSymbol.image;

      if (!chainOnly) {
        const bySym = byChainSym.get(sym);
        if (bySym?.image) return bySym.image;
      }
    }

    return "";
  };

  return { getTokenImage, getTokenSymbol, getNativeToken, ...rest };
}
